(function (skyward, $)
{
    "use strict";

    // dialogFocusManager is a singleton
    if (typeof skyward.dialogFocusManager === "object")
    {
        return;
    }

    /**
     * @typedef {Object} DialogFocusManager - An object to mange the tabbing/focus of ModalLayer
     * @property {function(ModalLayer):undefined} registerLayer - Registers a new ModalLayer with the DialogFocusManager and adds tab control elements
     * @property {function(ModalLayer):undefined} setFocus - Sets focus to the initial element within a ModalLayer
     * @property {function(ModalLayer, boolean):undefined} unregisterLayer - Unregisters a ModalLayer from the DialogFocusManager and removes tab control elements
     * @property {function(JQuery<HTMLElement>, boolean):undefined} toggleIgnoreElement - Toggles if an element should be ignored by the DialogFocusManager
     */

    skyward.dialogFocusManager = (function ()
    {
        const dialogFocusManager = {};

        const registeredLayers = [];
        const ignoreElementClass = "js-dialogFocusManagerIgnore";

        // This is a template that will be used to create the tab control elements within modal layers
        const tabControlTemplate = $("<div>", { tabindex: 0 });

        /**
        * Map the dialog Container with the before and after jQuery tab control elements
        *
        * @type { Object.<JQuery, {{ before: JQuery, after: JQuery }}>}
        */
        const tabControlElements = new WeakMap();

        /**
         * Registers a new ModalLayer with the DialogFocusManager and adds tab control elements
         *
         * @param {ModalLayer} layer - A ModalLayer to register
         */
        dialogFocusManager.registerLayer = function (layer)
        {
            registeredLayers.push(layer);
            addTabControlElements(layer);
        };

        /**
         * Unregisters a ModalLayer from the DialogFocusManager and removes tab control elements
         *
         * @param {ModalLayer} layer - A ModalLayer to unregister from the DialogFocusManager
         * @param {boolean} restoreFocus - Should focus be moved to the originalActiveElement of the ModalLayer
         */
        dialogFocusManager.unregisterLayer = function (layer, restoreFocus)
        {
            const layerIndex = registeredLayers.indexOf(layer);
            if (layerIndex > -1)
            {
                registeredLayers.splice(layerIndex, 1);
            }
            if (tabControlElements.has(layer))
            {
                const tabControls = tabControlElements.get(layer);
                tabControls.before.remove();
                tabControls.after.remove();
                tabControlElements.delete(layer);
            }
            if (restoreFocus === true && document.activeElement !== layer.originalActiveElement)
            {
                $(layer.originalActiveElement).focus();
            }
        };

        /**
         * Sets focus to the initial element within a ModalLayer
         *
         * @param {ModalLayer} layer - A ModalLayer to use as a basis for finding the element to set focus to
         */
        dialogFocusManager.setFocus = function (layer)
        {
            const focusElement = getInitialFocusElement(layer);
            // Work around jquery focus bug: https://stackoverflow.com/a/34401217
            // todo (jquery update) See if this can be removed
            const input = focusElement.get(0);
            if (input)
            {
                // Forces the cursor to be placed at the end of text on inputs
                if (input.tagName.toLowerCase() === "input" && input.type === "text")
                {
                    const length = input.value.length;
                    input.setSelectionRange(length, length);
                }
                input.focus();
            }
            return focusElement;
        };

        /**
         * Toggles if an element should be ignored by the DialogFocusManager
         * 
         * @param {JQuery<HTMLElement>} element - The element to ignore or unignore
         * @param {boolean} ignore - True if the element should be ignored
         */
        dialogFocusManager.toggleIgnoreElement = function (element, ignoreToggle)
        {
            element.toggleClass(ignoreElementClass, ignoreToggle);
        }

        // use focusin because it bubbles
        $(document).on("focusin", focusHandler);

        function focusHandler(event)
        {
            // we don't want to respond to the focus events we generate
            if (event.generatedByFocusManager) 
            {
                return;
            }

            var activeLayer = getActiveLayer();
            if (!activeLayer)
            {
                return;
            }

            var focusElement = $(event.target);
            if (focusElement.length)
            {
                if (focusElement.hasClass(ignoreElementClass))
                {
                    return;
                }

                // if focus is within the current dialog, do nothing
                if (activeLayer.contents.get(0).contains(focusElement[0]))
                {
                    return;
                }

                if (focusElement.closest(".exposedToModalLayer_" + activeLayer.uniqueId).length > 0)
                {
                    return;
                }
            }

            const tabControls = tabControlElements.get(activeLayer);
            let nextFocusElement;

            if (focusElement.is(tabControls.before))
            {
                nextFocusElement = activeLayer.contents.find(":tabbable").last();
            }
            else if (focusElement.is(tabControls.after))
            {
                nextFocusElement = activeLayer.contents.find(":tabbable").first();
            }

            if (typeof nextFocusElement === "undefined" || nextFocusElement.length === 0)
            {
                nextFocusElement = getInitialFocusElement(activeLayer);
            }

            nextFocusElement.trigger(
            {
                type: "focus",
                generatedByFocusManager: true
            });
        }

        function getInitialFocusElement(layer)
        {
            let elementToFocus;
            // Set preserveFocus to true to restore focus to the element that was focused before the layer was rendered. Set it to a jQuery-able value to focus a specific element.
            if (layer.options.preserveFocus === true)
            {
                // IE may view nearly any element as being active, even ones without .focus() methods.
                elementToFocus = $(layer.originalActiveElement).closest(function (elem) { return elem.focus !== undefined; });
            }
            else
            {
                elementToFocus = $(layer.options.initialFocusElement);
            }

            if (elementToFocus.length === 0)
            {
                elementToFocus = layer.contents.find(":tabbable").first();
            }

            // as a last resort, give the content div focus
            if (elementToFocus.length === 0)
            {
                elementToFocus = layer.contents.attr("tabindex", "0");
            }
            else
            {
                layer.contents.removeAttr("tabindex");
            }

            return elementToFocus;
        }

        /**
         * Adds focusable div elements before and after the dialog content.
         * These elements are used to trap focus within the dialog content area.
         *
         * @param {ModalLayer} layer - A ModalLayer to add the tab control elements to
         */
        function addTabControlElements(layer)
        {
            const beforeElement = tabControlTemplate.clone();
            const afterElement = tabControlTemplate.clone();

            beforeElement.insertBefore(layer.contents);
            afterElement.insertAfter(layer.contents);

            tabControlElements.set(layer, { before: beforeElement, after: afterElement });
        }

        function getActiveLayer()
        {
            for (let i = registeredLayers.length - 1; i >= 0; i--)
            {
                if (isDialogActive(registeredLayers[i]))
                {
                    return registeredLayers[i];                    
                }
            }
            return undefined;
        }

        function isDialogActive(layer)
        {
            return typeof layer === "object" && layer.onscreen && layer.container.length && layer.contents.length && document.body.contains(layer.contents[0]);
        }

        return dialogFocusManager;
    })();

}(window.skyward = window.skyward || {}, window.jQuery));